import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, ChevronDown, ChevronRight } from 'lucide-react';
import apiService from '../../apiService';

// Libellés FR des types de commerce (business_type). ⚠️ ≠ catégorie de produit.
const BUSINESS_TYPE_LABELS = {
  restaurant: '🍽️ Restaurant', fast_food: '🍔 Fast-food', supermarket: '🛒 Supermarché',
  pharmacy: '💊 Pharmacie', bakery: '🥖 Boulangerie', cafe: '☕ Café', bar: '🍺 Bar',
  butcher: '🥩 Boucherie', electronics: '📱 Électronique', clothing: '👕 Vêtements',
  hardware: '🔧 Quincaillerie', bookstore: '📚 Librairie', florist: '💐 Fleuriste',
  shop: '🏪 Boutique', fuel: '⛽ Station', laundry: '🧺 Blanchisserie',
  beauty_salon: '💇 Salon de beauté', gym: '🏋️ Salle de sport', hotel: '🏨 Hôtel', other: '📦 Autre',
};

export default function CategoryTemplatesPage() {
  const [list, setList] = useState([]);       // [{business_type, is_custom, categories}]
  const [selected, setSelected] = useState(null); // business_type
  const [draft, setDraft] = useState([]);     // categories en cours d'édition
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [expanded, setExpanded] = useState({}); // index → bool

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/delivery/category-templates', { userType: 'admin' });
      if (res.success) {
        const data = (res.data || []).sort((a, b) => (BUSINESS_TYPE_LABELS[a.business_type] || a.business_type).localeCompare(BUSINESS_TYPE_LABELS[b.business_type] || b.business_type));
        setList(data);
        if (!selected && data.length) selectType(data[0]);
      }
    } catch { setMsg('Impossible de charger les modèles'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []); // eslint-disable-line

  const selectType = (item) => {
    setSelected(item.business_type);
    // clone profond éditable
    setDraft((item.categories || []).map(c => ({ name: c.name, icon: c.icon || '', children: (c.children || []).map(ch => ({ name: ch.name, icon: ch.icon || '' })) })));
    setExpanded({});
    setMsg('');
  };

  const addCategory = () => setDraft(d => [...d, { name: '', icon: '', children: [] }]);
  const removeCategory = (i) => setDraft(d => d.filter((_, idx) => idx !== i));
  const setCatName = (i, v) => setDraft(d => d.map((c, idx) => idx === i ? { ...c, name: v } : c));
  const setCatIcon = (i, v) => setDraft(d => d.map((c, idx) => idx === i ? { ...c, icon: v } : c));
  const addChild = (i) => { setDraft(d => d.map((c, idx) => idx === i ? { ...c, children: [...c.children, { name: '', icon: '' }] } : c)); setExpanded(e => ({ ...e, [i]: true })); };
  const removeChild = (i, j) => setDraft(d => d.map((c, idx) => idx === i ? { ...c, children: c.children.filter((_, k) => k !== j) } : c));
  const setChildName = (i, j, v) => setDraft(d => d.map((c, idx) => idx === i ? { ...c, children: c.children.map((ch, k) => k === j ? { ...ch, name: v } : ch) } : c));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const categories = draft
        .map((c, i) => ({ name: c.name.trim(), icon: c.icon || null, sort_order: i, children: c.children.map((ch, j) => ({ name: ch.name.trim(), icon: ch.icon || null, sort_order: j })).filter(ch => ch.name) }))
        .filter(c => c.name);
      const res = await apiService.put(`/admin/delivery/category-templates/${selected}`, { categories }, { userType: 'admin' });
      if (res.success) { setMsg('✅ Modèle enregistré'); fetchList(); }
    } catch (e) { setMsg(e.message || 'Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem', color: '#9AA0A8' }}>⏳ Chargement…</div>;

  return (
    <div>
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1E2229' }}>🗂️ Modèles de rayons</h2>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#9AA0A8' }}>
          Arborescence de rayons (catégories de produits) proposée par type de commerce. Les fournisseurs l'importent puis la personnalisent.
        </p>
      </div>

      {msg && <div style={{ background: msg.startsWith('✅') ? '#ECFDF5' : '#fef2f2', color: msg.startsWith('✅') ? '#10B981' : '#dc2626', padding: '0.6rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' }}>{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '1.25rem', alignItems: 'start' }}>
        {/* Colonne types de commerce */}
        <div style={{ background: 'white', borderRadius: 14, padding: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {list.map(item => (
            <button key={item.business_type} onClick={() => selectType(item)} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left',
              padding: '0.6rem 0.75rem', border: 'none', borderRadius: 10, cursor: 'pointer', marginBottom: 2,
              background: selected === item.business_type ? '#FEE9E2' : 'transparent',
              color: selected === item.business_type ? '#C9350F' : '#2C313A', fontWeight: 600, fontSize: '0.85rem',
            }}>
              <span>{BUSINESS_TYPE_LABELS[item.business_type] || item.business_type}</span>
              {item.is_custom && <span style={{ fontSize: '0.65rem', color: '#FF7800' }}>modifié</span>}
            </button>
          ))}
        </div>

        {/* Éditeur */}
        <div style={{ background: 'white', borderRadius: 14, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1E2229' }}>{BUSINESS_TYPE_LABELS[selected] || selected}</h3>
            <button onClick={save} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#C9350F', color: 'white', border: 'none', borderRadius: 10, padding: '0.5rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
              <Save size={15} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>

          {draft.map((cat, i) => (
            <div key={i} style={{ border: '1px solid #E9EBEF', borderRadius: 12, padding: '0.6rem 0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setExpanded(e => ({ ...e, [i]: !e[i] }))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9AA0A8' }}>
                  {expanded[i] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                <input value={cat.icon} onChange={e => setCatIcon(i, e.target.value)} placeholder="🛒" maxLength={2}
                  style={{ width: 44, textAlign: 'center', padding: '0.45rem', border: '1px solid #E9EBEF', borderRadius: 8, fontSize: '1rem' }} />
                <input value={cat.name} onChange={e => setCatName(i, e.target.value)} placeholder="Nom du rayon"
                  style={{ flex: 1, padding: '0.45rem 0.6rem', border: '1px solid #E9EBEF', borderRadius: 8, fontSize: '0.9rem', fontWeight: 600 }} />
                <span style={{ fontSize: '0.7rem', color: '#9AA0A8' }}>{cat.children.length} sous-cat.</span>
                <button onClick={() => removeCategory(i)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626' }}><Trash2 size={16} /></button>
              </div>

              {expanded[i] && (
                <div style={{ marginTop: '0.6rem', paddingLeft: '2rem' }}>
                  {cat.children.map((ch, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ color: '#DCE0E6' }}>↳</span>
                      <input value={ch.name} onChange={e => setChildName(i, j, e.target.value)} placeholder="Sous-catégorie"
                        style={{ flex: 1, padding: '0.4rem 0.6rem', border: '1px solid #E9EBEF', borderRadius: 8, fontSize: '0.85rem' }} />
                      <button onClick={() => removeChild(i, j)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626' }}><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={() => addChild(i)} style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', color: '#C9350F', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, marginTop: 2 }}>
                    <Plus size={14} /> Ajouter une sous-catégorie
                  </button>
                </div>
              )}
            </div>
          ))}

          <button onClick={addCategory} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1.5px dashed #c7d2fe', background: '#FEE9E2', color: '#C9350F', borderRadius: 10, padding: '0.6rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', width: '100%', justifyContent: 'center' }}>
            <Plus size={16} /> Ajouter un rayon
          </button>
        </div>
      </div>
    </div>
  );
}
